import { Component, inject, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { Store } from '../models/store.model';
import { CountryService } from '../services/country.service';
import { StoreService } from '../services/store.service';
import { CompanyService } from '../services/company.service';
import { BrandService } from '../services/brand.service';
import { Brand } from '../models/brand.model';
import { deleteField } from 'firebase/firestore';
import {
  KONOBOX_LEGACY_PRICE_KEYS,
  KONOBOX_PACKAGE_SLOTS,
  KONOBOX_PRICING_LIMITS,
  KONOBOX_PRICING_SUMMARY_ERROR,
  KonoboxPreviewChip,
  KonoboxPricingFormErrors,
  konoboxPreviewChips,
  konoboxPricingFormFromStore,
  konoboxPricingToStored,
  validateKonoboxPricingForm
} from '../utils/konobox-pricing.util';

@Component({
  selector: 'app-store-management',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './store-management.component.html',
  styleUrls: ['./store-management.component.css']
})
export class StoreManagementComponent implements OnDestroy {
  private countryService = inject(CountryService);
  private storeService = inject(StoreService);
  private companyService = inject(CompanyService);
  private brandService = inject(BrandService);

  stores$: Observable<Store[]>;
  countries$: Observable<any[]>;
  storesCount$: Observable<number>;
  companiesList: any[] = [];
  rawStoresList: Store[] = [];

  private storesSubscription?: Subscription;

  newStore: any = {
    storeId: '',
    name: '',
    friendlyName: '',
    location: '',
    country: '',
    priceClassicStrip: 10,
    priceMultiCut: 10,
    ...konoboxPricingFormFromStore(null),
    companyIds: [],
    devices: []
  };
  isModalOpen = false;
  editingId: string | null = null;
  selectedStoreIds = new Set<string>();
  isSyncingCounts = false;
  isSaving = false;
  storeFormError = '';

  constructor() {
    this.countries$ = this.countryService.getCountries();
    this.companyService.getCompanies().subscribe(comps => this.companiesList = comps);

    const storesStream$ = this.storeService.getStores();
    this.storesSubscription = storesStream$.subscribe(stores => {
      this.rawStoresList = stores;
    });

    // All active stores (PhotoLavie and Konobox)
    this.stores$ = storesStream$.pipe(
      map((stores: any[]) => {
        const filtered = stores.filter(s => !s['isDeleted']);
        return (filtered as Store[]).sort((a, b) => (a.storeId || '').localeCompare(b.storeId || ''));
      })
    );

    this.storesCount$ = this.stores$.pipe(
      map(stores => stores.length)
    );

    // Automatically synchronize machine counts for all stores in the background
    this.syncCounts();
  }

  ngOnDestroy() {
    this.storesSubscription?.unsubscribe();
  }

  async syncCounts() {
    try {
      this.isSyncingCounts = true;
      await this.storeService.syncMachineCounts();
    } catch (e) {
      console.warn('Failed to sync store machine counts:', e);
    } finally {
      this.isSyncingCounts = false;
    }
  }

  openAddModal() {
    this.storeFormError = '';
    this.newStore = {
      storeId: '',
      name: '',
      friendlyName: '',
      location: '',
      country: '',
      priceClassicStrip: 10,
      priceMultiCut: 10,
      ...konoboxPricingFormFromStore(null),
      companyIds: [],
      devices: []
    };
    this.resetPricingFormState();
    this.editingId = null;
    this.isModalOpen = true;
  }

  async editStore(store: any) {
    this.storeFormError = '';
    let ids = store.companyIds || [];
    if (ids.length === 0 && store.companyId) {
      ids = [store.companyId];
    }
    let devices = store.devices || [];
    if (store.id || store.storeId) {
      devices = await this.storeService.getDevices(store.id || '', store.storeId);
    }
    const friendlyName = store.friendlyName || store.friendlyStoreName || '';

    const pricing = konoboxPricingFormFromStore(store);

    this.newStore = {
      ...store,
      friendlyName,
      priceClassicStrip: typeof store.priceClassicStrip === 'number' ? store.priceClassicStrip : 10,
      priceMultiCut: typeof store.priceMultiCut === 'number' ? store.priceMultiCut : 10,
      ...pricing,
      companyIds: ids,
      devices
    };
    this.resetPricingFormState();
    // A value that is already stored is shown as stored, so its errors (if any) are visible on open.
    this.pricingPriceTouched = !pricing.priceDefaulted;
    this.pricingCountTouched = this.pricingCountTouched.map(() => !pricing.countsDefaulted);
    this.editingId = store.id || null;
    this.isModalOpen = true;
  }

  cancelEdit() {
    this.newStore = {
      storeId: '',
      name: '',
      friendlyName: '',
      location: '',
      country: '',
      priceClassicStrip: 10,
      priceMultiCut: 10,
      ...konoboxPricingFormFromStore(null),
      companyIds: [],
      devices: []
    };
    this.resetPricingFormState();
    this.editingId = null;
    this.storeFormError = '';
    this.isSaving = false;
    this.isModalOpen = false;
  }

  // ---- Konobox per-song pricing form (KAN-697) ----
  readonly packageLimits = KONOBOX_PRICING_LIMITS;
  readonly packageSlots = Array.from({ length: KONOBOX_PACKAGE_SLOTS }, (_, i) => i);
  readonly pricingSummaryError = KONOBOX_PRICING_SUMMARY_ERROR;
  pricingPriceTouched = false;
  pricingCountTouched: boolean[] = this.packageSlots.map(() => false);
  pricingSubmitAttempted = false;

  private resetPricingFormState() {
    this.pricingPriceTouched = false;
    this.pricingCountTouched = this.packageSlots.map(() => false);
    this.pricingSubmitAttempted = false;
  }

  get pricingErrors(): KonoboxPricingFormErrors {
    return validateKonoboxPricingForm(this.newStore);
  }

  get pricingPreviewChips(): KonoboxPreviewChip[] {
    return konoboxPreviewChips(this.newStore);
  }

  get pricingDefaultedAny(): boolean {
    return !!(this.newStore.priceDefaulted || this.newStore.countsDefaulted);
  }

  get pricingDefaultedNote(): string {
    const { priceDefaulted, countsDefaulted } = this.newStore;
    if (priceDefaulted && countsDefaulted) {
      return 'Default pricing: $2.00 per song and 2, 4, 6 and 8 songs per package. These values are only stored for this store after you save.';
    }
    const which = priceDefaulted ? 'price per song' : 'song packages';
    return `The ${which} had not been set, so the default was filled in. It is stored for this store after you save.`;
  }

  showPriceError(): boolean {
    return (this.pricingPriceTouched || this.pricingSubmitAttempted) && this.pricingErrors.price !== null;
  }

  showCountError(i: number): boolean {
    return (this.pricingCountTouched[i] || this.pricingSubmitAttempted) && this.pricingErrors.counts[i] !== null;
  }

  markPriceTouched() {
    this.pricingPriceTouched = true;
  }

  markCountTouched(i: number) {
    this.pricingCountTouched[i] = true;
  }

  async saveStore() {
    if (this.isSaving) return;
    this.storeFormError = '';
    this.pricingSubmitAttempted = true;
    const trimmedId = (this.newStore.storeId || '').trim();
    const trimmedName = (this.newStore.name || '').trim();

    if (!trimmedId) {
      this.storeFormError = 'Store ID is required.';
      return;
    }
    if (!trimmedName) {
      this.storeFormError = 'Store Name is required.';
      return;
    }

    // Check duplicate storeId when creating new store or changing storeId
    const isDuplicate = this.rawStoresList.some(s =>
      !s.isDeleted &&
      (s.storeId || '').trim().toLowerCase() === trimmedId.toLowerCase() &&
      s.id !== this.editingId
    );
    if (isDuplicate) {
      this.storeFormError = `Store ID "${trimmedId}" already exists. Please choose a unique Store ID.`;
      return;
    }

    const pClassic = Number(this.newStore.priceClassicStrip ?? 10);
    const pMulti = Number(this.newStore.priceMultiCut ?? 10);

    if (this.pricingErrors.hasErrors) {
      this.storeFormError = KONOBOX_PRICING_SUMMARY_ERROR;
      return;
    }

    this.newStore.storeId = trimmedId;
    this.newStore.name = trimmedName;
    this.newStore.priceClassicStrip = pClassic;
    this.newStore.priceMultiCut = pMulti;

    const { priceDefaulted, countsDefaulted, ...storeFields } = this.newStore;
    const payload: any = {
      ...storeFields,
      ...konoboxPricingToStored({
        pricePerSong: this.newStore.pricePerSong,
        songCounts: this.newStore.songCounts
      })
    };
    // Legacy per-tier price fields are never written; an update also removes them from the document.
    for (const key of KONOBOX_LEGACY_PRICE_KEYS) {
      delete payload[key];
      if (this.editingId) payload[key] = deleteField();
    }

    this.isSaving = true;
    try {
      if (this.editingId) {
        await this.storeService.updateStore(this.editingId, payload);
        this.editingId = null;
      } else {
        await this.storeService.addStore(payload);
      }
      this.cancelEdit();
    } catch (e: any) {
      console.error('Failed to save store:', e);
      this.storeFormError = e.message || 'Failed to save store. Please try again.';
    } finally {
      this.isSaving = false;
    }
  }

  toggleSelection(id: string | undefined) {
    if (!id) return;
    if (this.selectedStoreIds.has(id)) {
      this.selectedStoreIds.delete(id);
    } else {
      this.selectedStoreIds.add(id);
    }
  }

  isAllSelected(stores: Store[]): boolean {
    if (!stores || stores.length === 0) return false;
    return stores.every(s => s.id && this.selectedStoreIds.has(s.id));
  }

  toggleAll(event: any, stores: Store[]) {
    if (!stores) return;
    if (event.target.checked) {
      stores.forEach(s => { if (s.id) this.selectedStoreIds.add(s.id); });
    } else {
      this.selectedStoreIds.clear();
    }
  }

  async deleteStore(id: string | undefined) {
    if (!id) return;
    if (confirm('Are you sure you want to delete this store?')) {
      await this.storeService.deleteStore(id);
    }
  }

  async deleteSelectedStores() {
    if (this.selectedStoreIds.size === 0) return;
    if (confirm(`Are you sure you want to delete ${this.selectedStoreIds.size} selected stores?`)) {
      await this.storeService.deleteStores(Array.from(this.selectedStoreIds));
      this.selectedStoreIds.clear();
    }
  }

  toggleCompany(companyId: string, event: any) {
    if (event.target.checked) {
      if (!this.newStore.companyIds) this.newStore.companyIds = [];
      this.newStore.companyIds.push(companyId);
    } else {
      if (this.newStore.companyIds) {
        this.newStore.companyIds = this.newStore.companyIds.filter((id: string) => id !== companyId);
      }
    }
  }

  isCompanySelected(companyId: string): boolean {
    return this.newStore.companyIds?.includes(companyId) ?? false;
  }

  getCompanyNames(store: any): string {
    let ids = store.companyIds;
    if (!ids || !Array.isArray(ids) || ids.length === 0) {
      if (store.companyId) {
        ids = [store.companyId];
      } else {
        return '';
      }
    }
    return ids.map((id: string) => {
      const c = this.companiesList.find(comp => comp.id === id);
      return c ? c.company : '';
    }).filter((n: string) => n).join(', ');
  }

  addDevice() {
    if (!this.newStore.devices) {
      this.newStore.devices = [];
    }
    this.newStore.devices.push({
      deviceId: '',
      deviceName: '',
      merchantId: '',
      refNo: '',
      terminalId: '',
      authCode: '',
      isNew: true
    });
  }

  removeDevice(index: number) {
    if (this.newStore.devices) {
      this.newStore.devices.splice(index, 1);
    }
  }
}